import Component from "@ember/component"
import { action, get, set } from "@ember/object"
import { later } from "@ember/runloop"
import { inject as service } from "@ember/service"
import type { Planbook } from "@shared/models/planbook"
import BelongTo from "cc-frontend/helpers/belong-to"
import PLANBOOK_CHANGE_TITLE from "cc-frontend/lib/actions/planbook/PLANBOOK_CHANGE_TITLE"
import PLANBOOK_SET_FLAG from "cc-frontend/lib/actions/planbook/PLANBOOK_SET_FLAG"
import PLANBOOK_UNDELETE from "cc-frontend/lib/actions/planbook/PLANBOOK_UNDELETE"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type NotifierService from "cc-frontend/services/notifier"
import type { EmberNotification } from "cc-frontend/services/notifier"
import type Rpc from "cc-frontend/services/rpc"
import type Session from "cc-frontend/services/session"
import { use } from "ember-could-get-used-to-this"
import _ from "lodash"
import moment from "moment"

export default class AdminSidebarPlanbook extends Component {
  @service declare dispatcher: Dispatcher
  @service declare session: Session
  @service declare rpc: Rpc
  @service declare router
  @service declare notifier: NotifierService

  canShowActionMenu = false
  declare model: Planbook

  @use rotationCalendar = new BelongTo(() => [this.model, "rotationCalendar"])

  @action
  impersonate(planbookId) {
    let newLocation = `${window.location.origin}/planbooks/${planbookId}/planner/week`
    get(this, "session").impersonateUserId(
      //  @ts-ignore. Convert to glimmer and get this typed
      get(this, "model.relationships.owner.data.id"),
      newLocation
    )
  }

  @action
  cleanUpCourseIds(planbookId) {
    this.notifier.success("", {
      notificationId: `cleanUpCourseIds${planbookId}`,
      title: `Deleted course ids being cleaned up...`,
      message: "You're the best.",
      type: "is-processing",
      duration: 2000,
    })

    this.rpc
      .execute("PLANBOOK_CLEAN_UP_DELETED_COURSES", {
        planbookId,
      })
      .then(() => {
        //  @ts-ignore
        let notification: EmberNotification = _.find(get(this, "notifier.notifications"), {
          notificationId: `cleanUpCourseIds${planbookId}`,
        })
        if (notification) {
          set(notification, "title", "Courses Ids Cleaned up!")
          set(notification, "type", "is-success")
        }
      })

    set(this, "canShowActionMenu", false)
  }

  @action
  transferPlanbook(planbookId) {
    set(this, "canShowActionMenu", false)

    // Delaying this so Ember can close the menu first. Ugh. We should use swal
    // or something
    later(() => {
      let newOwnerId = window.prompt("What user id should we transfer the planbook to?")
      if (newOwnerId) {
        this.notifier.success("", {
          notificationId: `transferPlanbook${planbookId}`,
          title: `Transferring Planbook...`,
          message: "You're the best.",
          duration: 3000,
          type: "is-processing",
        })
        get(this, "rpc")
          .execute("planbook_transfer_to_new_owner", {
            planbookId: planbookId,
            newOwnerId: newOwnerId,
          })
          .then(() => {
            //  @ts-ignore
            let notification: EmberNotification = _.find(get(this, "notifier.notifications"), {
              notificationId: `transferPlanbook${planbookId}`,
            })

            if (notification) {
              set(notification, "title", "Planbook transferred!")
              set(notification, "type", "is-success")
            }
          })
      }
    }, 150)
  }

  @action makeExactCopy(planbookId, existingOwnerId) {
    set(this, "canShowActionMenu", false)

    // Delaying this so Ember can close the menu first. Ugh. We should use swal
    // or something
    later(() => {
      let newOwnerId = window.prompt(
        "What user id should we transfer the copy of the planbook to (default is existing owner?",
        existingOwnerId
      )
      if (newOwnerId) {
        this.notifier.success("", {
          notificationId: `copyPlanbook${planbookId}`,
          title: `Copying Planbook...`,
          message: "Live. Laugh. Love.",
          duration: 3000,
          type: "is-processing",
        })
        this.rpc
          .planbook_admin_exact_copy({
            planbookId: planbookId,
            newOwnerId: newOwnerId,
          })
          .then(({ responseOk, result }) => {
            if (responseOk) {
              //  @ts-ignore
              let notification: EmberNotification = _.find(get(this, "notifier.notifications"), {
                notificationId: `copyPlanbook${planbookId}`,
              })

              if (notification) {
                set(notification, "title", "Planbook copied!")
                set(notification, "type", "is-success")
              }
              this.router.transitionTo("admin.planbook", result.newPlanbookId)
            } else {
              this.notifier.warning("", {
                notificationId: `copyPlanbook${planbookId}`,
                title: `Error copying planbook`,
                message: "Not enough living, laughing, or loving happened.",
                duration: 3000,
                type: "is-error",
              })
            }
          })
      }
    }, 150)
  }

  @action deletePlanbook(planbookId) {
    this.rpc.execute("PLANBOOK_DELETE", planbookId).then(() => {
      this.notifier.success("", {
        notificationId: `deletePlanbook${planbookId}`,
        title: `Deleted Planbook`,
        message: "Stay calm. Carry on.",
        duration: 3000,
        type: "is-success",
      })
    })
  }

  @action migratePlanbook(planbook: Planbook, migration: "unifiedLessons" | "legacyLessons") {
    set(this, "canShowActionMenu", false)

    const label =
      migration === "unifiedLessons"
        ? "Migrating to Unified Lessons"
        : "Undoing Migration to Unified Lessons"

    if (!window.confirm(`${label} for "${planbook.attributes.title}" rewrites its lessons.`)) return

    this.notifier.success("", {
      notificationId: `migratePlanbook${planbook.id}`,
      title: `${label}...`,
      message: "Hang tight.",
      duration: 3000,
      type: "is-processing",
    })

    this.rpc
      .planbook_migrate({
        planbookId: planbook.id,
        migration,
      })
      .then(({ responseOk }) => {
        if (responseOk) {
          //  @ts-ignore
          let notification: EmberNotification = _.find(get(this, "notifier.notifications"), {
            notificationId: `migratePlanbook${planbook.id}`,
          })
          if (notification) {
            set(notification, "title", `${label} complete!`)
            set(notification, "type", "is-success")
          }
        } else {
          this.notifier.warning("", {
            notificationId: `migratePlanbook${planbook.id}`,
            title: `Error ${label.toLowerCase()}`,
            message: "Please try again.",
            duration: 3000,
            type: "is-error",
          })
        }
      })
  }

  @action toggleStarred(planbook: Planbook) {
    set(this, "canShowActionMenu", false)

    const isStarred = planbook.attributes.flags?.isStarred === true
    const label = isStarred ? "no longer be" : "be"

    if (!window.confirm(`"${planbook.attributes.title}" will ${label} starred.`)) return

    this.dispatcher.dispatchAction(PLANBOOK_SET_FLAG, {
      planbookId: planbook.id,
      field: "isStarred",
      value: !isStarred,
    })
  }

  @action renamePlanbook(planbook) {
    let newPlanbookTitle = window.prompt(
      "What should we rename this planbook to?",
      planbook.attributes.title
    )
    if (newPlanbookTitle) {
      this.dispatcher.dispatchAction(PLANBOOK_CHANGE_TITLE, {
        originalTitle: planbook.attributes.title,
        title: newPlanbookTitle,
        planbookId: planbook.id,
      })
    }
  }

  @action
  undelete(planbookId) {
    this.notifier.success("", {
      notificationId: `unDelete${planbookId}`,
      title: `Undoing delete...`,
      message: "You're making someones life so much better now.",
      duration: 3000,
      type: "is-processing",
    })

    get(this, "dispatcher").dispatchAction(PLANBOOK_UNDELETE, {
      planbookId: planbookId,
      //  @ts-ignore
      userId: get(this, "model.relationships.owner.data.id"),
      classWebsiteIds: (this.model.relationships.classWebsites?.data ?? []).map(
        (linkage) => linkage.id
      ),
      timestamp: moment().format(),
    })
    //  @ts-ignore
    let notification: EmberNotification = _.find(get(this, "notifier.notifications"), {
      notificationId: `unDelete${planbookId}`,
    })
    if (notification) {
      set(notification, "title", "Undeleted!")
      set(notification, "type", "is-success")
    }
    set(this, "canShowActionMenu", false)
  }
}
